import { loadIgnoreList, isCorkSupplyName } from './ignoreListManager.js';

let currentEmailData = null;
let analysisResult = null;
let currentLanguage = null;
let currentAnalysisRequestId = 0;
let suggestionStatus = 'idle';
let suggestionError = null;
let selectedManualCustomer = null;
let customerSearchQuery = '';
let customerSearchStatus = 'idle';
let customerSearchResults = [];
let customerSearchError = null;
let currentCustomerSearchRequestId = 0;

const COMPANY_ENTITY_PATTERN = /\b(lda|ltda|sa|s\.a\.|sl|s\.l\.|slu|s\.l\.u\.|sas|srl|s\.r\.l\.|sro|s\.r\.o\.|gmbh|llc|inc|limited|ltd|corp|corporation|company|co\.|wines?|vinhos?|bodegas?|adega|cellars?|vineyards?|winery|proprietes?|distribution|viticultor|az\.?\s*agr\.?)\b/i;
const PERSON_NAME_CONNECTORS = new Set(['da', 'das', 'de', 'del', 'della', 'di', 'do', 'dos', 'du', 'e', 'la', 'le', 'van', 'von', 'y']);

const LANGUAGES = {
  FR: { code: 'FR', name: 'French', flag: 'https://flagcdn.com/w20/fr.png' },
  EN: { code: 'EN', name: 'English', flag: 'https://flagcdn.com/w20/gb.png' },
  PT: { code: 'PT', name: 'Portuguese', flag: 'https://flagcdn.com/w20/pt.png' },
  IT: { code: 'IT', name: 'Italian', flag: 'https://flagcdn.com/w20/it.png' },
  ES: { code: 'ES', name: 'Spanish', flag: 'https://flagcdn.com/w20/es.png' }
};

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    console.log("AI Disambiguation iniciado");
    // Carregar lista de ignorados antes de inicializar o app
    loadIgnoreList()
      .then(() => {
        console.log('✅ Lista de ignorados carregada com sucesso');
        initializeApp();
      })
      .catch((error) => {
        console.error('❌ Erro ao carregar lista de ignorados, continuando sem ela:', error);
        initializeApp();
      });
  }
});

function initializeApp() {
  loadCurrentEmailInfo();
  setupEventListeners();

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedLanguage = window.localStorage.getItem('cork_addin_language');
      const allowed = new Set(['FR', 'EN', 'PT', 'IT', 'ES']);
      if (savedLanguage && allowed.has(savedLanguage)) {
        currentLanguage = savedLanguage;
      }
    }
  } catch (e) {
    console.log('⚠️ Não foi possível carregar o idioma salvo', e);
  }

  document.addEventListener('click', (e) => {
    const wrapper = document.querySelector('.language-select-wrapper');
    const dropdown = document.getElementById('languageSelectDropdown');
    if (wrapper && dropdown && !wrapper.contains(e.target)) {
      dropdown.classList.remove('open');
    }
  });

  setTimeout(() => {
    runCompleteAnalysis();
  }, 1500);
}

function setupEventListeners() {
  if (Office.context.mailbox?.addHandlerAsync) {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      onItemChanged
    );
  }
}

function onItemChanged() {
  console.log("Email alterado");
  currentEmailData = null;
  analysisResult = null;
  suggestionStatus = 'idle';
  suggestionError = null;
  resetCustomerSearchState(true);
  loadCurrentEmailInfo();
  runCompleteAnalysis();
}

function loadCurrentEmailInfo() {
  const item = Office.context.mailbox.item;

  if (!item) {
    document.getElementById('mainContent').innerHTML =
      '<div class="no-data">Nenhum email selecionado</div>';
    return;
  }

  let stableItemId = item.itemId || '';
  try {
    if (
      stableItemId &&
      Office?.context?.mailbox &&
      typeof Office.context.mailbox.convertToRestId === 'function' &&
      Office?.MailboxEnums?.RestVersion?.v2_0
    ) {
      stableItemId = Office.context.mailbox.convertToRestId(
        stableItemId,
        Office.MailboxEnums.RestVersion.v2_0
      );
    }
  } catch (error) {
    console.log("⚠️ Não foi possível converter itemId para REST ID:", error);
  }

  currentEmailData = {
    subject: item.subject || 'Sem assunto',
    sender: item.from?.emailAddress || 'Desconhecido',
    displayName: item.from?.displayName || 'Desconhecido',
    dateReceived: item.dateTimeCreated?.toLocaleString('pt-PT') || 'Data desconhecida',
    conversationId: item.conversationId,
    itemId: stableItemId,
    rawItemId: item.itemId || '',
    internetMessageId: item.internetMessageId || ''
  };

  item.body.getAsync(Office.CoercionType.Text, (result) => {
    if (result.status === Office.AsyncResultStatus.Succeeded) {
      currentEmailData.body = result.value;
    }
  });
}

function isLikelyCompanyEntity(value) {
  const text = String(value || '').trim();
  if (!text || isCorkSupplyName(text)) return false;
  return COMPANY_ENTITY_PATTERN.test(text);
}

function isLikelyPersonName(value) {
  const text = String(value || '').trim();
  if (!text || isCorkSupplyName(text) || isLikelyCompanyEntity(text)) return false;
  if (/[0-9&/@]/.test(text)) return false;

  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length < 2 || tokens.length > 5) return false;

  let nameParts = 0;
  for (const token of tokens) {
    const clean = token.replace(/[.,;:()]/g, '');
    if (!clean) continue;
    const lower = clean.toLowerCase();
    if (PERSON_NAME_CONNECTORS.has(lower)) continue;
    if (!/^[A-ZÀ-Ý][A-Za-zÀ-ÿ'’-]+$/.test(clean)) return false;
    nameParts += 1;
  }

  return nameParts >= 2;
}

function resolveDisplayCompany(data) {
  const sapCode = String(data.customerSapCode || '').trim();
  const historyCode = String(data.buyerHistory?.orderIssuer ||
    data.buyerHistory?.raw?.['Emissor Ordem'] || '').trim();
  const historyOrg = data.buyerHistory?.orderIssuerName ||
    data.buyerHistory?.raw?.['Nome Emissor Ordem'] ||
    null;
  const customerOrg = data.customer?.company || null;

  const validHistoryOrg = historyOrg && !isCorkSupplyName(historyOrg) ? historyOrg : null;
  const validCustomerOrg = customerOrg && !isCorkSupplyName(customerOrg) ? customerOrg : null;

  // The name must belong to the SAP code we are showing: when the SAP record on
  // screen is the same row as the code, its "Nome Emissor Ordem" always wins.
  if (sapCode && historyCode && sapCode === historyCode && validHistoryOrg) {
    return validHistoryOrg;
  }

  if (!validHistoryOrg) {
    return validCustomerOrg || 'N/A';
  }

  if (
    validCustomerOrg &&
    validCustomerOrg !== validHistoryOrg &&
    isLikelyPersonName(validHistoryOrg) &&
    isLikelyCompanyEntity(validCustomerOrg)
  ) {
    return validCustomerOrg;
  }

  return validHistoryOrg;
}

function isTechnicalMaterialDescription(value) {
  const text = String(value || '').trim().toUpperCase();
  if (!text || text.includes(' ')) return false;
  if (!/^[A-Z0-9.+/-]{8,}$/.test(text)) return false;
  if (!/\d{2,3}X\d{3,4}/.test(text)) return false;
  return ((text.match(/[-+]/g) || []).length >= 2);
}

function resolveRenderedMaterialDescription(primaryDescription, fallbackDescription, emailProduct) {
  const primary = String(primaryDescription || '').trim();
  const fallback = String(fallbackDescription || '').trim();
  const emailText = String(emailProduct || '').trim();

  if (primary && fallback && isTechnicalMaterialDescription(primary) && !isTechnicalMaterialDescription(fallback)) {
    return fallback;
  }

  return primary || fallback || emailText || '-';
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Confidence thermometer: how sure the system is about the customer it picked.
// Bands come from measuring 104 verified orders: at 1.00 it never missed,
// between 0.90 and 0.95 it was wrong about 3 times in 10.
function getSelectionConfidence(data) {
  const candidates = data.buyerCandidates || [];
  const selected = candidates.find(c => c && c.selected) || candidates[0];
  if (!selected) return null;
  const raw = selected.selectionConfidence != null ? selected.selectionConfidence : selected.score;
  const value = Number(raw);
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : null;
}

const MATERIAL_SOURCE_LABELS = {
  sap_code_exact: 'código SAP citado no e-mail',
  customer_material_exact: 'referência de material do cliente',
  customer_price_exact: 'preço exato do cliente em SAP',
  customer_material_tiebreak: 'desempate por material do cliente',
  history_text_match: 'texto casado com o histórico SAP',
  end_customer_history: 'material já enviado ao destinatário final',
};

function getMaterialBand(value) {
  if (value == null) return { color: '#898781', label: 'sem indicador', hint: '' };
  if (value >= 0.9) return { color: '#0ca30c', label: 'Alta', hint: 'correspondência forte' };
  if (value >= 0.75) return { color: '#fab219', label: 'Média', hint: 'confirmar o material' };
  return { color: '#d03b3b', label: 'Baixa', hint: 'verificar manualmente' };
}

// Compact per-line thermometer for the products table. Colour never carries the
// meaning alone: the number is beside it and the tooltip names band and source.
function renderMaterialMeter(value, source) {
  const band = getMaterialBand(value);
  const sourceLabel = MATERIAL_SOURCE_LABELS[source] || (source ? String(source) : 'correspondência por IA');
  const shown = value == null ? '—' : Number(value).toFixed(2);
  const tip = value == null
    ? `Sem indicador de confiança (${sourceLabel})`
    : `Confiança do material ${shown} — ${band.label}: ${sourceLabel}`;
  return renderLineMeter(value, tip);
}

const MARKING_SOURCE_LABELS = {
  current_order: 'marcação indicada no e-mail',
  email_sap_match: 'marcação pedida no e-mail e encontrada no SAP',
  sap_history: 'marcação do histórico SAP',
  model_plate_choice: 'marca nova segundo o modelo (e-mail, anexos e placas do cliente)',
  learned_from_ov: 'placa aprendida de uma encomenda já registada no SAP com o mesmo pedido de marcação',
};

function renderMarkingMeter(value, source, evidence) {
  const band = getMaterialBand(value);
  const sourceLabel = MARKING_SOURCE_LABELS[source] || MARKING_SOURCE_LABELS.sap_history;
  const detail = evidence ? ` (${evidence})` : '';
  const shown = value == null ? '—' : Number(value).toFixed(2);
  const tip = value == null
    ? `Sem indicador de confiança da marcação${detail}`
    : `Confiança da marcação ${shown} — ${band.label}: ${sourceLabel}${detail}`;
  return renderLineMeter(value, tip);
}

const MARKING_PART_LABELS = { body: 'Corpo', top: 'Topo', 'top 1': 'Topo 1', 'top 2': 'Topo 2' };

function parseMarkingParts(marking) {
  const text = String(marking || '').trim();
  if (!text) return [];
  if (text.toUpperCase() === 'PLAIN') {
    return [{ label: '', value: 'Sem marcação (pedido no e-mail)' }];
  }
  return text
    .split(/;\s*/)
    .map(segment => segment.trim())
    .filter(Boolean)
    .map(segment => {
      const match = segment.match(/^(Body|Top(?: [12])?)\s*:\s*(.*)$/i);
      if (match) {
        const value = match[2].trim();
        if (!value) return null;
        return { label: MARKING_PART_LABELS[match[1].toLowerCase()] || match[1], value };
      }
      return { label: '', value: segment };
    })
    .filter(Boolean);
}

function markingMetaLabel(source, evidence) {
  if (source === 'email_sap_match') return 'pedida no e-mail, encontrada no SAP';
  if (source === 'model_plate_choice') return 'marca nova · segundo o modelo';
  if (source === 'learned_from_ov') {
    const order = String(evidence || '').match(/OV (\d+)/);
    return order ? `aprendida da OV ${order[1]}` : 'aprendida de uma OV';
  }
  if (source === 'current_order') {
    return /sem marca SAP/i.test(String(evidence || '')) ? 'indicada no e-mail · sem código SAP' : 'indicada no e-mail';
  }
  const counts = String(evidence || '').match(/(\d+)\s+de\s+(\d+)/);
  if (counts) return `${counts[1]} de ${counts[2]} no histórico`;
  return source === 'sap_history' ? 'histórico SAP' : '';
}

const SAP_PLATE_CODE_RE = /\(nº\s*\d+\)/;

const STRUCTURED_MARKING_RE = /^(Body|Top(?: [12])?)\s*:/i;

function isFreeTextMarking(marking) {
  const text = String(marking || '').trim();
  return Boolean(text) && text.toUpperCase() !== 'PLAIN' && !SAP_PLATE_CODE_RE.test(text) && !STRUCTURED_MARKING_RE.test(text);
}

function shownMarkingParts(marking) {
  return isFreeTextMarking(marking) ? [] : parseMarkingParts(marking);
}

function renderMarkingRow(item) {
  const parts = shownMarkingParts(item.marking);
  const suggestionHtml = renderMarkingSuggestion(item.markingSuggestion, item.markingSuggestionReason);
  if (parts.length === 0 && !suggestionHtml) return '';
  const confidence = parts.length > 0 ? item.markingConfidence : null;
  const band = getMaterialBand(confidence);
  const accent = confidence == null ? '#c3c2b7' : band.color;
  const meta = parts.length > 0 ? markingMetaLabel(item.markingSource, item.markingEvidence) : '';
  const meterHtml = parts.length > 0 ? renderMarkingMeter(item.markingConfidence, item.markingSource, item.markingEvidence) : '';
  const partsHtml = parts
    .map(part => `
        <div style="font-size:11.5px;color:#3d3c38;line-height:1.5;">
          ${part.label ? `<span style="font-weight:600;color:#575652;">${part.label}:</span> ` : ''}${escapeHtml(part.value)}
        </div>`)
    .join('');
  return `
      <tr>
        <td colspan="3" style="padding:2px 0 10px 0;">
          <div style="background:#f8f7f3;border-left:3px solid ${accent};border-radius:0 6px 6px 0;padding:7px 10px;">
            <div style="display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:4px;">
              <span style="font-size:10px;font-weight:700;letter-spacing:.6px;color:#575652;">MARCAÇÃO</span>
              ${meterHtml}
              ${meta ? `<span style="font-size:10.5px;color:#898781;">${meta}</span>` : ''}
            </div>
            ${partsHtml}
            ${suggestionHtml}
          </div>
        </td>
      </tr>`;
}

function markingSuggestionText(suggestion) {
  return parseMarkingParts(suggestion)
    .map(part => (part.label ? `${part.label}: ${part.value}` : part.value))
    .join(' · ');
}

function renderMarkingSuggestion(suggestion, reason) {
  const text = markingSuggestionText(suggestion);
  if (!text) return '';
  const why = reason || 'marca existente com o nome do anexo';
  return `
        <div style="margin-top:5px;padding-top:5px;border-top:1px dashed #d8d6cd;font-size:11px;color:#575652;line-height:1.5;">
          <span style="font-weight:700;color:#3f7f8f;">Sugestão SAP:</span> ${escapeHtml(text)}
          <span style="color:#898781;">(${escapeHtml(why)}; confirmar)</span>
        </div>`;
}

function renderLineMeter(value, tip) {
  const band = getMaterialBand(value);
  const shown = value == null ? '—' : Number(value).toFixed(2);
  const tubeTop = 2, tubeH = 18, bulbY = 26, bulbR = 4;
  const v = value == null ? 0 : Math.max(0, Math.min(1, value));
  const fillH = tubeH * v;
  const fillY = tubeTop + (tubeH - fillH);
  const colour = value == null ? '#c3c2b7' : band.color;
  return `
    <span title="${tip}" style="display:inline-flex;align-items:center;gap:4px;vertical-align:middle;line-height:1;">
      <svg width="10" height="32" viewBox="0 0 12 32" role="img" aria-label="${tip}" style="flex:none;display:block;">
        <rect x="3.5" y="${tubeTop}" width="5" height="${tubeH}" rx="2.5" fill="#eceae3"/>
        ${fillH > 0 ? `<rect x="3.5" y="${fillY.toFixed(1)}" width="5" height="${(fillH + 3).toFixed(1)}" rx="2.5" fill="${colour}"/>` : ''}
        <circle cx="6" cy="${bulbY}" r="${bulbR}" fill="${colour}"/>
        <rect x="3.5" y="${tubeTop}" width="5" height="${tubeH}" rx="2.5" fill="none" stroke="rgba(11,11,11,.13)"/>
        <circle cx="6" cy="${bulbY}" r="${bulbR}" fill="none" stroke="rgba(11,11,11,.13)"/>
      </svg>
      <span style="font-weight:600;color:${colour};font-size:10.5px;font-variant-numeric:tabular-nums;">${shown}</span>
    </span>`;
}

function getConfidenceBand(value) {
  if (value == null) return { color: '#898781', label: 'sem indicador', hint: '' };
  if (value >= 0.96) return { color: '#0ca30c', label: 'Alta', hint: 'pode seguir' };
  if (value >= 0.90) return { color: '#fab219', label: 'Média', hint: 'confirmar o cliente' };
  return { color: '#d03b3b', label: 'Baixa', hint: 'verificar manualmente' };
}

// Colour never carries the meaning alone - the number and the label go with it.
// A real thermometer: bulb at the bottom, the tube fills with the score, and a tick
// marks 0.96 - the level above which it never missed across the 104 verified orders.
function renderConfidenceMeter(value, options) {
  const opts = options || {};
  const band = getConfidenceBand(value);
  const tubeTop = 3, tubeH = 22, bulbY = 31, bulbR = 5;
  const v = value == null ? 0 : value;
  const fillH = tubeH * v;
  const fillY = tubeTop + (tubeH - fillH);
  const tickY = tubeTop + tubeH * (1 - 0.96);
  const colour = value == null ? '#b9b8b1' : band.color;
  const shown = value == null ? '—' : value.toFixed(2);
  const caption = value == null ? 'sem indicador' : band.label;
  const tip = value == null ? 'sem indicador de confiança' : `Confiança ${shown} — ${band.label}${band.hint ? ': ' + band.hint : ''}`;
  const tint = value == null ? 'rgba(11,11,11,.04)' : `${colour}12`;
  const edge = value == null ? 'rgba(11,11,11,.10)' : `${colour}30`;

  return `
    <span title="${tip}" style="display:inline-flex;align-items:center;gap:7px;padding:4px 9px 4px 6px;
                 background:${tint};border:1px solid ${edge};border-radius:8px;line-height:1;white-space:nowrap;">
      <svg width="13" height="38" viewBox="0 0 14 38" role="img" aria-label="${tip}" style="flex:none;display:block;">
        <rect x="4" y="${tubeTop}" width="6" height="${tubeH}" rx="3" fill="#eceae3"/>
        ${fillH > 0 ? `<rect x="4" y="${fillY.toFixed(1)}" width="6" height="${(fillH + 4).toFixed(1)}" rx="3" fill="${colour}"/>` : ''}
        <circle cx="7" cy="${bulbY}" r="${bulbR}" fill="${colour}"/>
        <rect x="4" y="${tubeTop}" width="6" height="${tubeH}" rx="3" fill="none" stroke="rgba(11,11,11,.13)"/>
        <circle cx="7" cy="${bulbY}" r="${bulbR}" fill="none" stroke="rgba(11,11,11,.13)"/>
        <line x1="10.5" y1="${tickY.toFixed(1)}" x2="13" y2="${tickY.toFixed(1)}" stroke="#6b6a66" stroke-width="1" opacity=".5"/>
      </svg>
      <span style="display:inline-flex;flex-direction:column;gap:1px;text-align:left;">
        <span style="font-weight:700;color:${colour};font-size:13.5px;font-variant-numeric:tabular-nums;letter-spacing:-.2px;">${shown}</span>
        <span style="color:#575652;font-size:10px;font-weight:600;">${caption}</span>
      </span>
    </span>`;
}

function resetCustomerSearchState(clearSelection = false) {
  customerSearchQuery = '';
  customerSearchStatus = 'idle';
  customerSearchResults = [];
  customerSearchError = null;
  currentCustomerSearchRequestId += 1;
  if (clearSelection) {
    selectedManualCustomer = null;
  }
}

function isNoHistoryAmbiguity(issueText) {
  const text = String(issueText || '').trim().toLowerCase();
  if (!text) return false;

  return (
    text.includes('not in history') ||
    text.includes('cannot match to history') ||
    text.includes('cannot match to any product in history') ||
    (text.includes('history') && text.includes('no exact match found')) ||
    (text.includes('history') && text.includes('no exact match')) ||
    (text.includes('history') && text.includes('does not match')) ||
    (text.includes('history') && text.includes("doesn't match")) ||
    (text.includes('history') && text.includes('do not match')) ||
    (text.includes('history') && text.includes("don't match")) ||
    (text.includes('history') && text.includes('doesnt match')) ||
    (text.includes('history') && text.includes('dont match'))
  );
}

function resolveAmbiguitySapDisplay(fallbackCode, issueText) {
  if (isNoHistoryAmbiguity(issueText)) {
    return 'Não encontrado no histórico';
  }
  return String(fallbackCode || '').trim();
}

function resolveAmbiguityDescription(fallbackDescription, issueText, emailProduct) {
  const fallback = String(fallbackDescription || '').trim();
  const emailText = String(emailProduct || '').trim();

  if (isNoHistoryAmbiguity(issueText)) {
    return emailText || fallback || '-';
  }

  return resolveRenderedMaterialDescription(fallback, emailText, '');
}

function hasNoHistoryAmbiguity(validationResults) {
  const ambiguities = validationResults?.ambiguities || [];
  return Array.isArray(ambiguities) && ambiguities.some((ambig) =>
    isNoHistoryAmbiguity(ambig?.issue || ambig?.ambiguity_reason || '')
  );
}

function getCustomerSearchCopy(language) {
  const isPortuguese = String(language || currentLanguage || '').toUpperCase() === 'PT';
  return isPortuguese ? {
    title: 'Pesquisar cliente',
    placeholder: 'Código ou nome do cliente',
    button: 'Pesquisar',
    searching: 'A pesquisar...',
    applying: 'A aplicar cliente...',
    noResults: 'Sem resultados.',
    selected: 'Cliente selecionado',
    retry: 'Tente outro termo.'
  } : {
    title: 'Search customer',
    placeholder: 'Customer code or name',
    button: 'Search',
    searching: 'Searching...',
    applying: 'Applying customer...',
    noResults: 'No results.',
    selected: 'Selected customer',
    retry: 'Try another term.'
  };
}

function renderCustomerSearchHtml(language) {
  const copy = getCustomerSearchCopy(language);
  const isBusy = customerSearchStatus === 'loading' || customerSearchStatus === 'applying';
  const buttonText = customerSearchStatus === 'loading' ? copy.searching : copy.button;
  const inputValue = escapeHtml(customerSearchQuery);
  const disabled = isBusy ? 'disabled' : '';

  const selectedHtml = selectedManualCustomer ? `
    <div class="customer-search-selected">
      <span>${copy.selected}</span>
      <strong>${escapeHtml(selectedManualCustomer.customer_sap_code || '')}</strong>
      ${escapeHtml(selectedManualCustomer.customer_company || '')}
    </div>
  ` : '';

  const errorHtml = customerSearchError ? `
    <div class="customer-search-error">${escapeHtml(customerSearchError)}</div>
  ` : '';

  const loadingHtml = customerSearchStatus === 'applying' ? `
    <div class="customer-search-status">${copy.applying}</div>
  ` : '';

  const resultsHtml = customerSearchResults.length > 0 ? `
    <div class="customer-search-results">
      ${customerSearchResults.map((customer, idx) => `
        <button
          type="button"
          class="customer-search-result"
          onclick="applyManualCustomerSelection(${idx})"
          ${disabled}
        >
          <strong>${escapeHtml(customer.customer_sap_code || '')}</strong>
          <span>${escapeHtml(customer.customer_company || '')}</span>
        </button>
      `).join('')}
    </div>
  ` : (
    customerSearchStatus === 'empty'
      ? `<div class="customer-search-status">${copy.noResults} ${copy.retry}</div>`
      : ''
  );

  return `
    <div class="customer-search-field">
      <div class="field-label">${copy.title}</div>
      <div class="customer-search-row">
        <input
          id="customerSearchTerm"
          class="customer-search-input"
          value="${inputValue}"
          placeholder="${copy.placeholder}"
          onkeydown="handleCustomerSearchKeydown(event)"
          ${disabled}
        />
        <button
          type="button"
          class="customer-search-button"
          onclick="searchCustomerAccounts()"
          ${disabled}
        >
          ${buttonText}
        </button>
      </div>
      ${selectedHtml}
      ${loadingHtml}
      ${errorHtml}
      ${resultsHtml}
    </div>
  `;
}

async function runCompleteAnalysis(options = {}) {
  if (!currentEmailData) {
    return;
  }

  const manualCustomerOverride = options.manualCustomerOverride || selectedManualCustomer || null;
  const preserveCurrentOnError = Boolean(options.preserveCurrentOnError);
  const requestId = ++currentAnalysisRequestId;
  suggestionStatus = 'idle';
  suggestionError = null;

  console.log("🔄 Iniciando análise completa...");

  const mainContent = document.getElementById('mainContent');
  if (preserveCurrentOnError && analysisResult) {
    customerSearchStatus = 'applying';
    customerSearchError = null;
    displaySimplifiedLayout(analysisResult, currentLanguage);
  } else {
    mainContent.innerHTML = '<div class="loading">Analisando email...</div>';
  }

  const cachedFn = window.fetchCachedBuyerAnalysis;
  if (!manualCustomerOverride && currentEmailData.internetMessageId && typeof cachedFn === 'function') {
    const cached = await cachedFn(currentEmailData.internetMessageId);
    if (requestId !== currentAnalysisRequestId) {
      console.log("⚠️ Descarta resultado de cache obsoleto", { requestId, currentAnalysisRequestId });
      return;
    }
    if (cached) {
      analysisResult = cached;
      customerSearchStatus = 'idle';
      customerSearchError = null;
      console.log("✅ Análise pré-calculada carregada do cache");
      displaySimplifiedLayout(analysisResult, currentLanguage);
      return;
    }
  }

  if (!currentEmailData.body) {
    await loadEmailBody();
  }

  await loadEmailAttachments();

  try {
    const fetchFn = window.fetchRealBuyerAnalysis || fetchRealBuyerAnalysis;

    if (typeof fetchFn !== 'function') {
      throw new Error('API de análise não disponível');
    }

    const result = await fetchFn(currentEmailData, currentLanguage, manualCustomerOverride);

    if (requestId !== currentAnalysisRequestId) {
      console.log("⚠️ Descarta resultado de análise obsoleto", { requestId, currentAnalysisRequestId });
      return;
    }

    analysisResult = result;
    if (manualCustomerOverride) {
      selectedManualCustomer = manualCustomerOverride;
      customerSearchStatus = 'applied';
    } else {
      customerSearchStatus = 'idle';
    }
    customerSearchError = null;
    console.log("✅ Análise concluída:", analysisResult);

    displaySimplifiedLayout(analysisResult, currentLanguage);

  } catch (error) {
    if (requestId !== currentAnalysisRequestId) {
      console.log("⚠️ Descarta erro de análise obsoleto", { requestId, currentAnalysisRequestId });
      return;
    }
    console.error("❌ Erro na análise:", error);
    if (preserveCurrentOnError && analysisResult) {
      customerSearchStatus = 'error';
      customerSearchError = error.message || 'Não foi possível aplicar o cliente selecionado.';
      displaySimplifiedLayout(analysisResult, currentLanguage);
      return;
    }
    mainContent.innerHTML = `
      <div class="no-data">
        Erro na análise: ${error.message || 'Não foi possível analisar o email'}
      </div>
    `;
  }
}

async function loadEmailBody() {
  return new Promise((resolve) => {
    Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        currentEmailData.body = result.value;
      }
      resolve();
    });
  });
}

async function loadEmailAttachments() {
  return new Promise((resolve) => {
    try {
      // Check if we already have valid cached attachments for this session
      if (currentEmailData && currentEmailData.attachments && currentEmailData.attachments.length > 0) {
        // Verify if we have content (base64) for the attachments that require it
        const hasCachedContent = currentEmailData.attachments.some(a => a.contentBase64);
        if (hasCachedContent) {
          console.log("♻️ Usando anexos em cache local (" + currentEmailData.attachments.length + ")");
          resolve();
          return;
        }
      }
      const item = Office.context.mailbox.item;

      if (!item || !item.attachments) {
        console.log("⚠️ Anexos não disponíveis via Office.js");
        currentEmailData.attachments = [];
        currentEmailData.attachments_context = "";
        resolve();
        return;
      }

      // Formatos de documento suportados
      const supportedExtensions = [
        '.pdf', '.docx', '.doc',  // Documentos
        '.xlsx', '.xls', '.csv',  // Planilhas
        '.pptx', '.ppt',  // Apresentações
        '.odt', '.ods', '.odp',  // OpenDocument
        '.txt', '.rtf'  // Texto simples
      ];
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
      const isAttachedImage = (att, name) => !att.isInline && imageExtensions.some(ext => name.endsWith(ext));

      const getAttachments = () => {
        return new Promise((resolveAttachments) => {
          if (typeof item.attachments.getAsync === 'function') {
            item.attachments.getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolveAttachments(result.value || []);
              } else {
                console.log("⚠️ Erro ao buscar anexos:", result.error);
                resolveAttachments([]);
              }
            });
          } else if (Array.isArray(item.attachments)) {
            resolveAttachments(item.attachments);
          } else {
            resolveAttachments([]);
          }
        });
      };

      getAttachments().then(async (attachments) => {
        console.log(`📎 Encontrados ${attachments.length} anexo(s) no email`);

        const relevantAttachments = attachments.filter(att => {
          const name = (att.name || "").toLowerCase();
          return supportedExtensions.some(ext => name.endsWith(ext)) || isAttachedImage(att, name);
        });

        console.log(`📎 ${relevantAttachments.length} anexo(s) relevante(s)`);

        // Try to read attachment content using getAttachmentContentAsync
        const attachmentsWithContent = [];

        for (const att of relevantAttachments) {
          try {
            const content = await new Promise((resolveContent, rejectContent) => {
              if (typeof item.getAttachmentContentAsync === 'function') {
                item.getAttachmentContentAsync(att.id, (result) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolveContent(result.value);
                  } else {
                    console.log(`⚠️ Erro ao ler anexo ${att.name}:`, result.error);
                    resolveContent(null);
                  }
                });
              } else {
                console.log("⚠️ getAttachmentContentAsync não disponível");
                resolveContent(null);
              }
            });

            if (content && content.content) {
              attachmentsWithContent.push({
                name: att.name || "",
                size: att.size || 0,
                attachmentType: att.attachmentType || "",
                contentBase64: content.content,
                format: content.format  // "base64" or other
              });
              console.log(`✅ Leu conteúdo do anexo: ${att.name} (${content.content.length} chars base64)`);
            } else {
              attachmentsWithContent.push({
                name: att.name || "",
                size: att.size || 0,
                attachmentType: att.attachmentType || ""
              });
              console.log(`⚠️ Anexo sem conteúdo: ${att.name}`);
            }
          } catch (err) {
            console.log(`❌ Erro ao processar anexo ${att.name}:`, err);
            attachmentsWithContent.push({
              name: att.name || "",
              size: att.size || 0,
              attachmentType: att.attachmentType || ""
            });
          }
        }

        currentEmailData.attachments = attachmentsWithContent;
        currentEmailData.attachments_context = "";

        console.log("📦 Anexos preparados para envio:", currentEmailData.attachments.map(a => ({
          name: a.name,
          hasContent: !!a.contentBase64,
          contentLength: a.contentBase64?.length || 0
        })));

        resolve();
      });
    } catch (error) {
      console.error("❌ Erro ao carregar anexos:", error);
      currentEmailData.attachments = [];
      currentEmailData.attachments_context = "";
      resolve();
    }
  });
}

function displaySimplifiedLayout(data, language) {
  const mainContent = document.getElementById('mainContent');

  window.currentBuyerAnalysis = data;

  console.log('📊 Dados completos:', data);
  console.log('🔍 buyerHistory:', data.buyerHistory);
  console.log('🔍 buyerHistory.orderIssuer:', data.buyerHistory?.orderIssuer);
  console.log('🔍 buyerHistory.material:', data.buyerHistory?.material);
  console.log('🔍 buyerHistory.raw:', data.buyerHistory?.raw);

  // SAP ONLY - Customer Name uses receiverName (Nome Recebedor Mercadoria from SAP)
  const customerName = (() => {
    const name = data.buyerHistory?.receiverName ||
      data.buyerHistory?.raw?.['Nome Recebedor Mercadoria'] || null;
    if (!name || isCorkSupplyName(name)) return 'N/A';
    return name;
  })();

  const company = resolveDisplayCompany(data);

  const sapNumber = data.customerSapCode ||
    data.buyerHistory?.orderIssuer ||
    data.buyerHistory?.raw?.['Emissor Ordem'] ||
    'N/A';


  const selectionConfidence = getSelectionConfidence(data);
  const confidenceMeterHtml = renderConfidenceMeter(selectionConfidence);
  const sourceDocument = data.csiDocument
    ? { label: 'Documento CSI', value: data.csiDocument }
    : (data.salesforceOrder ? { label: 'Ordem Salesforce', value: data.salesforceOrder } : null);
  const csiDocumentHtml = sourceDocument
    ? `<div class="field">
        <div class="field-label">${sourceDocument.label}</div>
        <div class="field-value" style="font-variant-numeric:tabular-nums;">${escapeHtml(sourceDocument.value)}</div>
      </div>`
    : '';

  const sapReceiver = data.buyerHistory?.receiverCode ||
    data.buyerHistory?.raw?.['Recebedor Mercadoria'] ||
    null;

  const effectiveLanguage = language || (data.emailSuggestion && data.emailSuggestion.language) || null;

  const productsHtml = generateProductsTable(data.orderDetails, data.buyerHistory, data.buyerHistoryOrders, data.historyMatch);

  const requiresReply = data.emailSuggestion && (data.emailSuggestion.subject || data.emailSuggestion.body);
  const customerSearchHtml = data.isOrder ? renderCustomerSearchHtml(effectiveLanguage) : '';

  const generatedResponse = requiresReply ? (data.emailSuggestion.body || '') : '';
  const generatedSubject = requiresReply ? (data.emailSuggestion.subject || `Re: ${currentEmailData?.subject || ''}`) : '';
  const canGenerateReply = Boolean(data.isOrder);
  const generateButtonText = suggestionStatus === 'loading' ? 'A gerar...' : 'Gerar resposta';
  const generateButtonDisabled = !canGenerateReply || suggestionStatus === 'loading' ? 'disabled' : '';
  const suggestionErrorHtml = suggestionError
    ? `<div class="suggestion-error">${escapeHtml(suggestionError)}</div>`
    : '';
  const escapedGeneratedSubject = escapeHtml(generatedSubject);
  const escapedGeneratedResponse = escapeHtml(generatedResponse);

  mainContent.innerHTML = `
    <div class="content-card">
      <div class="field">
        <div class="field-label">Company</div>
        <div class="field-value">${company}</div>
      </div>

      <div class="field">
        <div class="field-label">SAP Customer Code</div>
        <div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
          <span class="field-value" style="font-variant-numeric:tabular-nums;">${sapNumber}</span>
          ${confidenceMeterHtml}
        </div>
      </div>

      ${csiDocumentHtml}

      ${productsHtml}

      <div class="requires-reply-field">
        <div class="field-label">Requires Reply</div>
        <span class="badge">${requiresReply ? 'Yes' : 'No'}</span>
      </div>

      ${customerSearchHtml}

      <div class="field">
        <div class="field-label">Language</div>
        ${getLanguageSelectHtml(effectiveLanguage)}
      </div>
      
      <div>
        <div class="response-label">Generated Response</div>
        ${canGenerateReply ? `
          <button
            class="generate-response-button"
            onclick="generateResponseSuggestion()"
            ${generateButtonDisabled}
          >
            ${generateButtonText}
          </button>
          ${suggestionErrorHtml}
        ` : ''}
        <input
          class="response-subject"
          id="generatedSubject"
          placeholder="Assunto da resposta..."
          value="${escapedGeneratedSubject}"
        />
        <textarea 
          class="response-textarea" 
          id="generatedResponse"
          placeholder="A resposta gerada aparecerá aqui..."
        >${escapedGeneratedResponse}</textarea>
      </div>

      <div class="action-buttons">
        <button class="send-button" onclick="sendResponse()">
          Send Response
        </button>
        <button class="print-button" onclick="printTaskpane()">
          Imprimir
        </button>
      </div>
    </div>
  `;
}

function normalizeProductLabel(value) {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function resolveProductIndex(products, label, fallbackIdx) {
  const target = normalizeProductLabel(label);
  if (!target) return fallbackIdx;
  const exactIdx = products.findIndex(product => normalizeProductLabel(product) === target);
  if (exactIdx >= 0) return exactIdx;
  const fuzzyIdx = products.findIndex(product => {
    const candidate = normalizeProductLabel(product);
    return candidate && (candidate.includes(target) || target.includes(candidate));
  });
  return fuzzyIdx >= 0 ? fuzzyIdx : fallbackIdx;
}

function lineValue(values, details, productIdx) {
  return values.length === details.products.length ? (values[productIdx] || '') : '';
}

function markingFields(entry) {
  return {
    marking: entry.sap_marking || entry.sapMarking || '',
    markingConfidence: typeof entry.marking_confidence === 'number' ? entry.marking_confidence : null,
    markingSource: entry.marking_source || null,
    markingEvidence: entry.marking_evidence || null,
    markingSuggestion: entry.marking_suggestion || null,
    markingSuggestionReason: entry.marking_suggestion_reason || null
  };
}

function matchItem(match, productIdx, details) {
  const sapCode = match.sap_code || match.sapCode || lineValue(details.materialCodes, details, productIdx);
  const lineDescription = lineValue(details.materialDescriptions, details, productIdx);
  return {
    order: productIdx,
    code: sapCode || '',
    description: sapCode
      ? resolveRenderedMaterialDescription(match.sap_description, lineDescription, match.email_product)
      : resolveRenderedMaterialDescription(lineDescription, match.email_product, match.sap_description),
    quantity: match.quantity || details.quantities[productIdx] || '-',
    ...markingFields(match),
    confidence: typeof match.confidence === 'number' ? match.confidence : null,
    matchSource: match.match_source || match.matchSource || null,
    isValidated: Boolean(sapCode)
  };
}

function ambiguityItem(ambig, productIdx, details, claimedCodes) {
  const lineCode = lineValue(details.materialCodes, details, productIdx);
  const fallbackDescription = lineValue(details.materialDescriptions, details, productIdx) || details.products[productIdx] || '';
  const issueText = ambig.issue || ambig.ambiguity_reason || 'Not matched in SAP';
  return {
    order: productIdx,
    code: resolveAmbiguitySapDisplay(claimedCodes.has(lineCode) ? '' : lineCode, issueText),
    description: resolveAmbiguityDescription(fallbackDescription, issueText, ambig.email_product || ambig.product),
    quantity: ambig.quantity || details.quantities[productIdx] || '-',
    ...markingFields(ambig),
    isValidated: false,
    issue: issueText
  };
}

function unvalidatedItems(details) {
  const labels = details.products.length > 0 ? details.products : details.materialDescriptions;
  return labels.map((label, idx) => ({
    order: idx,
    code: details.materialCodes[idx] || '',
    description: label || details.materialDescriptions[idx] || '-',
    quantity: details.quantities[idx] || '-',
    marking: '',
    isValidated: false
  }));
}

function buildProductItems(orderDetails) {
  const validationResults = orderDetails?.validationResults || orderDetails?.validation_results || {};
  const matches = validationResults.matches || [];
  const ambiguities = validationResults.ambiguities || [];
  const details = {
    products: orderDetails?.products || [],
    quantities: orderDetails?.quantities || [],
    materialCodes: orderDetails?.materialCodes || orderDetails?.material_codes || [],
    materialDescriptions: orderDetails?.materialDescriptions || orderDetails?.material_descriptions || []
  };
  if (matches.length === 0 && ambiguities.length === 0) return unvalidatedItems(details);
  const matchItems = matches.map((match, idx) =>
    matchItem(match, resolveProductIndex(details.products, match.email_product || match.product, idx), details));
  const claimedCodes = new Set(matchItems.map(item => item.code).filter(Boolean));
  const ambiguityItems = ambiguities.map((ambig, idx) =>
    ambiguityItem(ambig, resolveProductIndex(details.products, ambig.email_product || ambig.product, idx), details, claimedCodes));
  return matchItems.concat(ambiguityItems).sort((a, b) => a.order - b.order);
}

function alignLineValues(values, items, orderDetails) {
  if (values.length === 0) return values;
  const productCount = (orderDetails?.products || []).length;
  const materialCodes = (orderDetails?.materialCodes || orderDetails?.material_codes || []).map(String);
  if (values.length === productCount) return items.map(item => values[item.order] || '-');
  if (values.length === materialCodes.length) return items.map(item => values[materialCodes.indexOf(String(item.code))] || '-');
  return items.length > 1 && values.length === 1 ? Array(items.length).fill(values[0]) : values;
}

function generateProductsTable(orderDetails, buyerHistory, buyerHistoryOrders, historyMatch) {
  const items = buildProductItems(orderDetails);
  console.log("chamando generateProductsTable com:", { orderDetails, buyerHistory, buyerHistoryOrders, historyMatch, items });

  let rows = '';
  items.forEach(item => {
    const qty = typeof item.quantity === 'number'
      ? item.quantity.toLocaleString('pt-PT')
      : item.quantity;

    // Apply dark red style for non-validated products
    const titleAttr = item.isValidated ? '' : `title="${item.issue || 'Not matched in SAP'}"`;

    const meterHtml = item.isValidated
      ? `<div style="margin-top:2px;">${renderMaterialMeter(item.confidence, item.matchSource)}</div>`
      : '';
    const markingRowHtml = renderMarkingRow(item);
    const cellCss = (item.isValidated ? '' : 'color:#8B0000;')
      + (markingRowHtml ? 'border-bottom:none;padding-bottom:2px;' : '');
    const tdStyle = cellCss ? `style="${cellCss}"` : '';
    rows += `
      <tr ${titleAttr}>
        <td ${tdStyle}>${item.code}${meterHtml}</td>
        <td ${tdStyle}>${item.description}</td>
        <td ${tdStyle}>${qty}</td>
      </tr>
      ${markingRowHtml}
    `;
  });

  return `
    <table class="products-table">
      <thead>
        <tr>
          <th>SAP Code</th>
          <th>Description</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
}

function getLanguageSelectHtml(selectedLanguage) {
  const selected = selectedLanguage ? LANGUAGES[selectedLanguage] : null;
  const displayText = selected ? `${selected.name} (${selected.code})` : 'Selecione o idioma';
  const displayFlag = selected ? selected.flag : '';

  let optionsHtml = '';
  Object.values(LANGUAGES).forEach(lang => {
    optionsHtml += `
      <div class="language-option" data-value="${lang.code}" onclick="selectLanguage('${lang.code}')">
        <img src="${lang.flag}" alt="${lang.name}" class="flag-img" onerror="this.style.display='none'">
        <span>${lang.name} (${lang.code})</span>
      </div>
    `;
  });

  return `
    <div class="language-select-wrapper">
      <div class="language-select-trigger" onclick="toggleLanguageDropdown()" id="languageSelectTrigger">
        ${displayFlag ? `<img src="${displayFlag}" alt="" class="flag-img" onerror="this.style.display='none'">` : ''}
        <span>${displayText}</span>
        <span class="arrow">▼</span>
      </div>
      <div class="language-select-dropdown" id="languageSelectDropdown">
        ${optionsHtml}
      </div>
    </div>
  `;
}

function toggleLanguageDropdown() {
  const dropdown = document.getElementById('languageSelectDropdown');
  if (dropdown) {
    dropdown.classList.toggle('open');
  }
}

function selectLanguage(languageCode) {
  currentLanguage = languageCode;

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('cork_addin_language', languageCode);
    }
  } catch (e) {
    console.log('⚠️ Não foi possível salvar o idioma', e);
  }

  const dropdown = document.getElementById('languageSelectDropdown');
  if (dropdown) {
    dropdown.classList.remove('open');
  }

  runCompleteAnalysis();
}

function handleCustomerSearchKeydown(event) {
  if (event.key === 'Enter') {
    event.preventDefault();
    searchCustomerAccounts();
  }
}

async function searchCustomerAccounts() {
  const input = document.getElementById('customerSearchTerm');
  const query = (input?.value || '').trim();
  customerSearchQuery = query;

  if (query.length < 2) {
    customerSearchStatus = 'error';
    customerSearchError = 'Introduza pelo menos 2 caracteres para pesquisar.';
    customerSearchResults = [];
    if (analysisResult) {
      displaySimplifiedLayout(analysisResult, currentLanguage);
    }
    return;
  }

  const requestId = ++currentCustomerSearchRequestId;
  customerSearchStatus = 'loading';
  customerSearchError = null;
  customerSearchResults = [];
  if (analysisResult) {
    displaySimplifiedLayout(analysisResult, currentLanguage);
  }

  try {
    const searchFn = window.searchBuyerCustomers;
    if (typeof searchFn !== 'function') {
      throw new Error('API de pesquisa de clientes não disponível');
    }

    const results = await searchFn(query, 5);
    if (requestId !== currentCustomerSearchRequestId) {
      return;
    }

    customerSearchResults = Array.isArray(results) ? results.slice(0, 5) : [];
    customerSearchStatus = customerSearchResults.length > 0 ? 'results' : 'empty';
    customerSearchError = null;
    if (analysisResult) {
      displaySimplifiedLayout(analysisResult, currentLanguage);
    }
  } catch (error) {
    if (requestId !== currentCustomerSearchRequestId) {
      return;
    }
    console.error('❌ Erro ao pesquisar clientes:', error);
    customerSearchStatus = 'error';
    customerSearchError = error.message || 'Não foi possível pesquisar clientes.';
    customerSearchResults = [];
    if (analysisResult) {
      displaySimplifiedLayout(analysisResult, currentLanguage);
    }
  }
}

async function applyManualCustomerSelection(index) {
  const customer = customerSearchResults[index];
  if (!customer || !customer.customer_sap_code) {
    return;
  }

  selectedManualCustomer = {
    customer_sap_code: String(customer.customer_sap_code || '').trim(),
    customer_company: String(customer.customer_company || '').trim(),
    source: 'addin_manual_customer_search'
  };
  customerSearchStatus = 'applying';
  customerSearchError = null;

  await runCompleteAnalysis({
    manualCustomerOverride: selectedManualCustomer,
    preserveCurrentOnError: true
  });
}

function sendResponse() {
  const responseText = document.getElementById('generatedResponse')?.value || '';
  const responseSubject = document.getElementById('generatedSubject')?.value || '';

  if (!responseText.trim()) {
    alert('A resposta está vazia. Por favor, escreva algo antes de enviar.');
    return;
  }

  try {
    Office.context.mailbox.displayNewMessageForm({
      toRecipients: currentEmailData?.sender ? [currentEmailData.sender] : [],
      subject: responseSubject.trim() || `Re: ${currentEmailData?.subject || ''}`,
      htmlBody: responseText.replace(/\n/g, '<br>')
    });

    console.log('✅ Nova janela de email aberta com a resposta');
  } catch (error) {
    console.error('❌ Erro ao abrir email:', error);
    alert('Erro ao abrir a janela de email. Tente novamente.');
  }
}

async function generateResponseSuggestion() {
  if (!analysisResult || !currentEmailData) {
    return;
  }

  if (!analysisResult.isOrder) {
    suggestionStatus = 'error';
    suggestionError = 'A resposta só pode ser gerada para emails classificados como ordem.';
    displaySimplifiedLayout(analysisResult, currentLanguage);
    return;
  }

  const requestId = currentAnalysisRequestId;
  suggestionStatus = 'loading';
  suggestionError = null;
  displaySimplifiedLayout(analysisResult, currentLanguage);

  try {
    const generateFn = window.generateBuyerEmailSuggestion;
    if (typeof generateFn !== 'function') {
      throw new Error('API de sugestão não disponível');
    }

    const suggestion = await generateFn(currentEmailData, analysisResult, currentLanguage);
    if (requestId !== currentAnalysisRequestId) {
      console.log("⚠️ Descarta sugestão de resposta obsoleta", { requestId, currentAnalysisRequestId });
      return;
    }

    analysisResult = {
      ...analysisResult,
      emailSuggestion: suggestion
    };
    suggestionStatus = 'success';
    suggestionError = null;
    displaySimplifiedLayout(analysisResult, currentLanguage);
  } catch (error) {
    if (requestId !== currentAnalysisRequestId) {
      return;
    }
    console.error('❌ Erro ao gerar resposta:', error);
    suggestionStatus = 'error';
    suggestionError = error.message || 'Não foi possível gerar a resposta.';
    displaySimplifiedLayout(analysisResult, currentLanguage);
  }
}

function printTaskpane() {
  const buyerData = window.currentBuyerAnalysis;
  if (!buyerData) {
    window.print();
    return;
  }

  const printContent = generatePrintContent(buyerData);

  // Criar elemento temporário para impressão
  const printDiv = document.createElement('div');
  printDiv.id = 'printContent';
  printDiv.innerHTML = printContent;
  printDiv.style.display = 'none';
  document.body.appendChild(printDiv);

  // Adicionar estilos de impressão se não existirem
  if (!document.getElementById('printStyles')) {
    const style = document.createElement('style');
    style.id = 'printStyles';
    style.textContent = `
      @media print {
        body > *:not(#printContent) { display: none !important; }
        #printContent { display: block !important; }
      }
    `;
    document.head.appendChild(style);
  }

  // Imprimir e remover depois
  window.print();

  setTimeout(() => {
    printDiv.remove();
  }, 1000);
}

const PRINT_EMPHASIS_LABEL_RE = /^(urgência|importante)$/i;

function splitPrintLabel(text) {
  const clean = String(text || '').trim();
  const match = clean.match(/^([^:]{2,40}):\s*([\s\S]+)$/);
  if (!match || match[1].trim().split(/\s+/).length > 4) return { label: '', value: clean };
  return { label: match[1].trim(), value: match[2].trim() };
}

function renderPrintListItem(text) {
  const { label, value } = splitPrintLabel(text);
  const labelHtml = label ? `<strong>${escapeHtml(label)}:</strong> ` : '';
  const valueHtml = PRINT_EMPHASIS_LABEL_RE.test(label) ? `<strong>${escapeHtml(value)}</strong>` : escapeHtml(value);
  return `<li style="margin-bottom:4px;">${labelHtml}${valueHtml}</li>`;
}

function renderPrintList(lines) {
  const entries = (lines || []).filter(line => typeof line === 'string' && line.trim());
  if (entries.length === 0) return '-';
  return `<ul style="margin:0;padding-left:18px;">${entries.map(renderPrintListItem).join('')}</ul>`;
}

function renderPrintMarkingGroup(entry, showCode) {
  const heading = showCode && entry.code ? `<div><strong>${escapeHtml(entry.code)}</strong></div>` : '';
  return `${heading}${renderPrintList(entry.lines)}`;
}

function generatePrintContent(data) {
  console.log('🖨️ Gerando conteúdo de impressão com os dados:', data);

  const company = resolveDisplayCompany(data);

  const sapNumber = data.customerSapCode ||
    data.buyerHistory?.orderIssuer ||
    data.buyerHistory?.raw?.['Emissor Ordem'] ||
    'N/A';

  const printConfidenceHtml = renderConfidenceMeter(getSelectionConfidence(data), { width: 120 });
  const printSourceDocument = data.csiDocument
    ? { label: 'Documento CSI', value: data.csiDocument }
    : (data.salesforceOrder ? { label: 'Ordem Salesforce', value: data.salesforceOrder } : null);

  const validationResults = data.orderDetails?.validationResults || data.orderDetails?.validation_results || {};
  const items = buildProductItems(data.orderDetails);

  const emailPriceDisplays = Array.isArray(data.orderDetails?.priceDisplays) ? data.orderDetails.priceDisplays : [];
  const renderedPriceDisplays = emailPriceDisplays
    .map((value) => {
      if (value === null || value === undefined) return null;
      const text = String(value).trim();
      return text || null;
    })
    .filter(Boolean);
  const emailPrices = Array.isArray(data.orderDetails?.prices) ? data.orderDetails.prices : [];
  const renderedEmailPrices = emailPrices
    .map((priceValue) => {
      if (priceValue === null || priceValue === undefined) return null;
      if (typeof priceValue === 'number' && Number.isFinite(priceValue)) {
        // Preserve precision for small unit prices (e.g., 0,06250) in print output.
        if (Math.abs(priceValue) < 1) {
          return priceValue.toLocaleString('pt-PT', { minimumFractionDigits: 5, maximumFractionDigits: 5 });
        }
        return priceValue.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
      }
      const text = String(priceValue).trim();
      return text || null;
    })
    .filter(Boolean);
  const suppressHistoryPrice = hasNoHistoryAmbiguity(validationResults);
  const historyPrice = suppressHistoryPrice ? null : (data.buyerHistory?.price || data.buyerHistory?.netPrice || null);
  const linePriceDisplays = alignLineValues(renderedPriceDisplays, items, data.orderDetails);
  const lineEmailPrices = alignLineValues(renderedEmailPrices, items, data.orderDetails);
  const price = linePriceDisplays.length > 0
    ? linePriceDisplays.join('<br>')
    : lineEmailPrices.length > 0
    ? lineEmailPrices.join('<br>')
    : (historyPrice || '-');

  // Use email-extracted dates from orderDetails (not SAP data)
  const emailDates = data.orderDetails?.dates || [];
  const dateDisplays = (data.orderDetails?.dateDisplays || []).filter(text => typeof text === 'string' && text.trim());
  const shownDates = dateDisplays.length > 0 ? dateDisplays : emailDates;
  const deliveryDate = shownDates.length > 0 ? shownDates.join('<br>') : '-';

  // Show detailed marking/branding text, with fallback to explicit body/top brand fields.
  const historyRaw = data.buyerHistory?.raw || {};
  const markingRaw =
    data.buyerHistory?.markingType ??
    historyRaw['Tipo Marcação'] ??
    historyRaw['tipo_marcacao'] ??
    null;

  const normalizeMarkingText = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Sim' : null;
    const text = String(value).trim();
    if (!text) return null;
    const lowered = text.toLowerCase();
    if (['nan', 'null', 'undefined', 'nat', 'n/a', 'none', '-', 'no', 'na'].includes(lowered)) return null;
    return text;
  };

  const explicitMarking = normalizeMarkingText(markingRaw);
  const bodyBrand = normalizeMarkingText(
    data.buyerHistory?.bodyMark ??
    historyRaw['Descrição Marca Corpo'] ??
    historyRaw['Descrição Marca corpo'] ??
    historyRaw['descricao_marca_corpo'] ??
    historyRaw['Marca Corpo'] ??
    historyRaw['marca_corpo']
  );
  const topBrand12 = normalizeMarkingText(
    data.buyerHistory?.topMark1Plus2 ??
    historyRaw['Descrição Marca Topo 1+2'] ??
    historyRaw['Descrição Marca topo 1+2'] ??
    historyRaw['descricao_marca_topo_1+2'] ??
    historyRaw['Marca Topo 1+2'] ??
    historyRaw['marca_topo_1+2']
  );
  const topBrand1 = normalizeMarkingText(
    data.buyerHistory?.topMark1 ??
    historyRaw['Descrição Marca 1º Topo'] ??
    historyRaw['Descrição Marca 1º topo'] ??
    historyRaw['descricao_marca_1_topo'] ??
    historyRaw['descricao_marca_topo_1'] ??
    historyRaw['Marca 1º Topo'] ??
    historyRaw['marca_1_topo']
  );
  const topBrand2 = normalizeMarkingText(
    data.buyerHistory?.topMark2 ??
    historyRaw['Descrição Marca 2º Topo'] ??
    historyRaw['Descrição Marca 2º topo'] ??
    historyRaw['descricao_marca_2_topo'] ??
    historyRaw['descricao_marca_topo_2'] ??
    historyRaw['Marca 2º Topo'] ??
    historyRaw['marca_2_topo']
  );

  const composedMarkingParts = [];
  if (bodyBrand) composedMarkingParts.push(`Body Brand / Reference: ${bodyBrand}`);
  if (topBrand12) {
    composedMarkingParts.push(`Top Brand: ${topBrand12}`);
  } else {
    if (topBrand1) composedMarkingParts.push(`Top Brand 1: ${topBrand1}`);
    if (topBrand2) composedMarkingParts.push(`Top Brand 2: ${topBrand2}`);
  }
  const markedItems = items
    .map(item => {
      const lines = shownMarkingParts(item.marking)
        .map(part => (part.label ? `${part.label}: ${part.value}` : part.value));
      const suggestion = markingSuggestionText(item.markingSuggestion);
      if (suggestion) lines.push(`Sugestão SAP: ${suggestion}`);
      return lines.length > 0 ? { code: item.code || item.description, lines } : null;
    })
    .filter(Boolean);
  const hidesFreeText = items.some(item => isFreeTextMarking(item.marking));
  const marking = markedItems.length > 0
    ? markedItems.map(entry => renderPrintMarkingGroup(entry, markedItems.length > 1)).join('')
    : (hidesFreeText ? '-' : (explicitMarking ? escapeHtml(explicitMarking) : renderPrintList(composedMarkingParts)));

  const emailReferences = data.orderDetails?.references || [];
  const observationNotes = Array.isArray(data.orderDetails?.observationNotes) ? data.orderDetails.observationNotes : [];
  const referenceLines = emailReferences.length > 0 ? [`Referências: ${emailReferences.join(', ')}`] : [];
  const observations = renderPrintList(referenceLines.concat(observationNotes));

  // Concatenar valores com quebra de linha - SAP codes, descriptions, and quantities
  const sapMaterialsHtml = items.map(item => item.code || '-').join('<br>') || '-';
  const descriptionsHtml = items.map(item => item.description).join('<br>');
  const quantitiesHtml = items.map(item => {
    const qty = item.quantity;
    return typeof qty === 'number' ? qty.toLocaleString('pt-PT') : qty;
  }).join('<br>');

  return `
<div style="font-family: Arial, sans-serif; padding: 20px;">
  <table style="border-collapse: collapse; width: 100%; max-width: 800px; margin: 20px auto;">
    <tbody>
      <tr><td style="border: 1px solid #000; padding: 10px; width: 40%; font-weight: bold; background-color: #f0f0f0;">Lista</td><td style="border: 1px solid #000; padding: 10px; width: 60%;">${data.emailInfo.subject}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Company</td><td style="border: 1px solid #000; padding: 10px;">${company}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">SAP Customer Code</td><td style="border: 1px solid #000; padding: 10px;">${sapNumber}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Confiança</td><td style="border: 1px solid #000; padding: 10px;">${printConfidenceHtml}</td></tr>
      ${printSourceDocument ? `<tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">${printSourceDocument.label}</td><td style="border: 1px solid #000; padding: 10px;">${escapeHtml(printSourceDocument.value)}</td></tr>` : ''}
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">SAP Code</td><td style="border: 1px solid #000; padding: 10px;">${sapMaterialsHtml}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Description</td><td style="border: 1px solid #000; padding: 10px;">${descriptionsHtml}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Amount</td><td style="border: 1px solid #000; padding: 10px;">${quantitiesHtml}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Preço</td><td style="border: 1px solid #000; padding: 10px;">${price}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Data Entrega</td><td style="border: 1px solid #000; padding: 10px;">${deliveryDate}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Marcação</td><td style="border: 1px solid #000; padding: 10px;">${marking}</td></tr>
      <tr><td style="border: 1px solid #000; padding: 10px; font-weight: bold; background-color: #f0f0f0;">Observações</td><td style="border: 1px solid #000; padding: 10px;">${observations}</td></tr>
    </tbody>
  </table>
</div>
  `;
}

window.sendResponse = sendResponse;
window.searchCustomerAccounts = searchCustomerAccounts;
window.applyManualCustomerSelection = applyManualCustomerSelection;
window.handleCustomerSearchKeydown = handleCustomerSearchKeydown;
window.generateResponseSuggestion = generateResponseSuggestion;
window.printTaskpane = printTaskpane;

window.toggleLanguageDropdown = toggleLanguageDropdown;
window.selectLanguage = selectLanguage;
